/**
 * cell_id 로 IXD 셀을 찾을 때 쓰는 CSS 셀렉터 생성.
 *
 * cell_id 는 `{period}:{lineItem}:{members}:{accumulatedType}` 4-segment 다.
 * 문장영역 태그(textBlock 계열)는 기간 개념이 없어 period 가 빈 정본을 갖는데
 * (`:entity00111999_RestrictionOnUse::`), IXD 표는 그 fact 를 어느 기간 열엔가
 * 그려야 해서 period 가 채워진 id (`CURRENT:entity00111999_RestrictionOnUse::`)
 * 로 렌더한다. 그래서 정본 그대로 찾으면 0건이 되고, 문장 클릭이 무반응이 된다.
 */

import { ATTR } from "@/utils/dom-attrs";

/** cell_id 정규형의 세그먼트 수 — `{period}:{lineItem}:{members}:{accumulatedType}`. */
const SEGMENT_COUNT = 4;

/**
 * 따옴표로 감싼 CSS 속성값 안에서 escape 가 필요한 문자.
 *
 * `CSS.escape` 를 안 쓰는 이유는 **jsdom 에 `CSS` 전역이 없어서**다(이 프로젝트의 jsdom
 * 에서 `window.CSS === undefined` — 확인함). 셀렉터 조립은 순수 문자열 연산이라 DOM 없이
 * 테스트하고 싶은 자리라 여기서만 값 규칙을 쓴다.
 *
 * `"` · `\` 뿐 아니라 제어문자·개행도 hex 로 escape 한다 — 값 안에 들어가면 CSS 문자열이
 * 파싱 에러가 되어 `querySelectorAll` 이 **throw** 한다. 셀을 못 찾는 것과 화면이 죽는
 * 것은 다르다.
 */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

const escapeAttrValue = (value: string): string =>
  value
    .replace(/["\\]/g, "\\$&")
    .replace(CONTROL_CHARS, (c) => `\\${c.charCodeAt(0).toString(16)} `);

/**
 * period 세그먼트만 빈 **정규형** cell_id — 문장영역 태그처럼 기간이 없는 값.
 *
 * 세그먼트 수까지 본다. 아래 접미사 일치는 "맨 앞 콜론이 lineItem 경계" 라는 전제 위에
 * 서는데, 세그먼트가 모자란 id(`:Assets:`)는 그 전제가 깨져 다른 세그먼트 경계에
 * 걸린다 — `"CURRENT:X:Assets:".endsWith(":Assets:")` 가 참이라 axis 를 lineItem 으로
 * 오인한다. 비정규 id 는 접미사 폴백을 타지 않고 정확 일치까지만 시도한다.
 */
export const isPeriodlessCellId = (cellId: string): boolean =>
  cellId.startsWith(":") && cellId.split(":").length === SEGMENT_COUNT;

/**
 * cell_id 의 period 세그먼트(`CURRENT` | `PREVIOUS` | `BEFORE_PREVIOUS`).
 * 기간 없는 정본(`:X::`)이면 `null`.
 */
export const periodOfCellId = (cellId: string): string | null =>
  cellId.split(":")[0] || null;

/**
 * `[attr="value"]` 한 짝. 값 escape 규칙의 **유일한 수정 지점**이다.
 *
 * 이 파일 밖에도 같은 조립이 필요한 자리가 있어(예: 연동 팝오버는 정규화 id 를
 * `data-cell-id` 에서만 찾아야 해서 `exactCellIdSelector` 를 못 쓴다) 손으로 붙이게
 * 두면 escape 규칙이 두 벌이 된다.
 */
export const attrEqualsSelector = (attr: string, value: string): string =>
  `[${attr}="${escapeAttrValue(value)}"]`;

/**
 * 한 라인아이템의 제목 헤더(th)를 찾는 셀렉터. `data-header-ids` 토큰
 * (`getHeaderLookupKeys` 가 lineItem id 를 싣는다)으로 찾는다.
 */
export const lineItemHeaderSelector = (lineItemId: string): string =>
  `th[data-header-ids~="${escapeAttrValue(lineItemId)}"]`;

/** 정확 일치 셀렉터. 일반 cell_id 는 이것만으로 찾힌다. */
export const exactCellIdSelector = (cellId: string): string =>
  [ATTR.cellId, ATTR.sourceCellId]
    .map((attr) => attrEqualsSelector(attr, cellId))
    .join(",");

/**
 * period 를 무시하는 셀렉터. period 가 빈 id 는 그 자체가 렌더 id 의 접미사이므로
 * 접미사 일치로 찾는다 (`CURRENT:X::` 는 `:X::` 로 끝난다).
 *
 * 맨 앞 콜론이 경계 역할을 해서 다른 라인아이템으로 새지 않는다 — `:Assets::` 는
 * `CURRENT:SubAssets::` 와 일치하지 않는다(`Assets` 앞이 콜론이 아니다).
 * members·accumulatedType 세그먼트는 접미사에 그대로 들어 있어 함께 일치해야 한다.
 */
export const periodAgnosticCellIdSelector = (cellId: string): string => {
  const escaped = escapeAttrValue(cellId);
  return `[${ATTR.cellId}$="${escaped}"],[${ATTR.sourceCellId}$="${escaped}"]`;
};
